/**
 * Разделы приложения.
 *
 * Один список — и он единственный источник правды о навигации. Меню
 * рисуется из него, адрес разбирается по нему, страница выбирается им
 * же. Добавить раздел — дописать сюда одну запись; ни меню, ни оболочка
 * об этом не узнают, потому что не знают о разделах ничего конкретного.
 *
 * Так и просили: добавить раздел не должно означать правку четырёх
 * файлов. «Статистика» это подтвердила дважды: одна запись — появилась,
 * одна запись — ушла на «Главную».
 *
 * Значки нарисованы здесь, а не взяты шрифтом: их немного, они мелкие, и
 * ради них тянуть библиотеку — это лишние сто килобайт на первый экран
 * ради того, что рисуется двадцатью строками.
 */

import { type ComponentType, type JSX } from 'react';
import { lazyPage } from './lazy-page.js';

/*
 * Терминал грузится отдельным куском.
 *
 * Он весит больше всего остального приложения вместе взятого, а
 * открывается не первым: после входа человек попадает на «Главную».
 * Держать его в основном куске значит заставить ждать график того, кто
 * пришёл посмотреть сводку.
 */
const ChartPage = lazyPage(() =>
  import('../pages/ChartPage.js').then((m) => ({ default: m.ChartPage })),
);
const RadarPage = lazyPage(() => import('../pages/RadarPage.js').then((m) => ({ default: m.RadarPage })));
const WorldPage = lazyPage(() => import('../pages/WorldPage.js').then((m) => ({ default: m.WorldPage })));
const HomePage = lazyPage(() => import('../pages/HomePage.js').then((m) => ({ default: m.HomePage })));
const NotificationsPage = lazyPage(() =>
  import('../pages/NotificationsPage.js').then((m) => ({ default: m.NotificationsPage })),
);
const AccountsPage = lazyPage(() => import('../pages/AccountsPage.js').then((m) => ({ default: m.AccountsPage })));
const AccountPage = lazyPage(() =>
  import('../pages/AccountPage.js').then((m) => ({ default: m.AccountPage })),
);

/**
 * Место раздела в меню.
 *
 * Слева — то, куда ходят работать; справа — то, что относится к самому
 * человеку: его уведомления и его учётная запись. Разделение не
 * косметическое: правая группа не растёт от новых возможностей
 * платформы, левая растёт именно от них.
 */
export type SectionSide = 'main' | 'user';

export interface Section {
  /** Совпадает с адресом: `#/home`. */
  readonly id: string;
  readonly label: string;
  readonly side: SectionSide;
  readonly icon: (props: { className?: string }) => JSX.Element;
  readonly page: ComponentType;
  /**
   * Раздел занимает весь экран сам.
   *
   * У терминала своя разметка во всю высоту, со своими панелями и
   * полосой состояния внизу. Обычные страницы вместо этого лежат в
   * колонке с полями — иначе сводка растянулась бы во всю ширину
   * монитора и читать её было бы нечем.
   */
  readonly bare?: boolean;
}

/* ---------------------------------------------------------- значки */

function HomeIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 10.5 12 4l8 6.5V19a1 1 0 0 1-1 1h-4v-5h-6v5H5a1 1 0 0 1-1-1v-8.5Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChartIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 4v16h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path
        d="m7 15 3.5-4.5 3 2.5L19 7"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RadarIcon({ className }: { className?: string }): JSX.Element {
  // Круг радара с лучом и точкой-находкой: то, что раздел и делает.
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="12" cy="12" r="4.5" stroke="currentColor" strokeWidth="1.2" opacity="0.6" />
      <path d="M12 12 18 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="15.5" cy="14.5" r="1.4" fill="currentColor" />
    </svg>
  );
}

function WorldIcon({ className }: { className?: string }): JSX.Element {
  // Глобус: мир людей и их систем — рядом с радаром, но не похож на его круг с лучом.
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M3.5 12h17M12 3.5c2.4 2.3 3.6 5.1 3.6 8.5s-1.2 6.2-3.6 8.5c-2.4-2.3-3.6-5.1-3.6-8.5s1.2-6.2 3.6-8.5Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  );
}

function BellIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M6.5 10a5.5 5.5 0 0 1 11 0c0 3 .7 4.6 1.4 5.5H5.1c.7-.9 1.4-2.5 1.4-5.5Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M10 18.5a2 2 0 0 0 4 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="8.5" r="3.5" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M5 20c.6-3.4 3.4-5.5 7-5.5s6.4 2.1 7 5.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function WalletIcon({ className }: { className?: string }): JSX.Element {
  // Кошелёк: счета на площадках — деньги человека, демо и настоящие.
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 8.5A2.5 2.5 0 0 1 6.5 6H18a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6.5A2.5 2.5 0 0 1 4 16.5v-8Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M4 9.5h16" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="16" cy="14.2" r="1.3" fill="currentColor" />
    </svg>
  );
}

/* ---------------------------------------------------------- разделы */

export const SECTIONS: readonly Section[] = [
  { id: 'home', label: 'Главная', side: 'main', icon: HomeIcon, page: HomePage, bare: true },
  { id: 'chart', label: 'График', side: 'main', icon: ChartIcon, page: ChartPage, bare: true },
  { id: 'radar', label: 'Радар', side: 'main', icon: RadarIcon, page: RadarPage, bare: true },
  { id: 'world', label: 'Мир', side: 'main', icon: WorldIcon, page: WorldPage, bare: true },
  { id: 'accounts', label: 'Счета', side: 'user', icon: WalletIcon, page: AccountsPage, bare: true },
  {
    id: 'notifications',
    label: 'Уведомления',
    side: 'user',
    icon: BellIcon,
    page: NotificationsPage,
    bare: true,
  },
  { id: 'account', label: 'Личный кабинет', side: 'user', icon: UserIcon, page: AccountPage, bare: true },
];

/** Куда попадает вошедший. */
export const DEFAULT_SECTION = 'home';

export function findSection(id: string): Section | undefined {
  return SECTIONS.find((section) => section.id === id);
}

export function sectionsOn(side: SectionSide): readonly Section[] {
  return SECTIONS.filter((section) => section.side === side);
}
